import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Leaf, Sun, Wind, Snowflake, Plus, Trash2, Save, Eye, EyeOff, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { BRAND } from "@/config/brand";

interface SeasonalWellnessItem {
  content: string;
}

interface SeasonalWellness {
  id?: string;
  user_id?: string;
  season: string;
  title: string;
  description: string;
  hygiene_vie: SeasonalWellnessItem[];
  conseils_alimentaires: SeasonalWellnessItem[];
  soutiens_naturels: SeasonalWellnessItem[];
  routine_bien_etre: SeasonalWellnessItem[];
  is_active: boolean;
  is_section_active: boolean;
}

const seasonConfig = {
  spring: {
    name: "Printemps",
    icon: Leaf,
    color: "text-green-600",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    borderColor: "border-green-500",
  },
  summer: {
    name: "Été",
    icon: Sun,
    color: "text-yellow-600",
    bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
    borderColor: "border-yellow-500",
  },
  autumn: {
    name: "Automne",
    icon: Wind,
    color: "text-orange-600",
    bgColor: "bg-orange-50 dark:bg-orange-900/20",
    borderColor: "border-orange-500",
  },
  winter: {
    name: "Hiver",
    icon: Snowflake,
    color: "text-blue-600",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    borderColor: "border-blue-500",
  },
};

const defaultSeasonData = (season: string): SeasonalWellness => ({
  season,
  title: `Protocole ${seasonConfig[season as keyof typeof seasonConfig].name}`,
  description: "",
  hygiene_vie: [{ content: "" }],
  conseils_alimentaires: [{ content: "" }],
  soutiens_naturels: [{ content: "" }],
  routine_bien_etre: [{ content: "" }],
  is_active: false,
  is_section_active: false,
});

const SeasonalWellnessManagement = () => {
  const [isSectionActive, setIsSectionActive] = useState(false);
  const [seasonalData, setSeasonalData] = useState<Record<string, SeasonalWellness>>({
    spring: defaultSeasonData("spring"),
    summer: defaultSeasonData("summer"),
    autumn: defaultSeasonData("autumn"),
    winter: defaultSeasonData("winter"),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);

      const { data, error } = await supabase
        .from("seasonal_wellness")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        const newSeasonalData = { ...seasonalData };
        let sectionActive = false;

        data.forEach((item: any) => {
          newSeasonalData[item.season] = {
            ...item,
            hygiene_vie: item.hygiene_vie || [{ content: "" }],
            conseils_alimentaires: item.conseils_alimentaires || [{ content: "" }],
            soutiens_naturels: item.soutiens_naturels || [{ content: "" }],
            routine_bien_etre: item.routine_bien_etre || [{ content: "" }],
          };
          if (item.is_section_active) sectionActive = true;
        });

        setSeasonalData(newSeasonalData);
        setIsSectionActive(sectionActive);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleSectionToggle = async (checked: boolean) => {
    if (!userId) return;
    setIsSectionActive(checked);

    // Update all seasons with the new section active status
    const newSeasonalData = { ...seasonalData };
    Object.keys(newSeasonalData).forEach((season) => {
      newSeasonalData[season].is_section_active = checked;
    });
    setSeasonalData(newSeasonalData);

    // Save to database - create records if they don't exist
    for (const season of Object.keys(newSeasonalData)) {
      const data = newSeasonalData[season];
      const payload = {
        user_id: userId,
        season,
        title: data.title,
        description: data.description,
        hygiene_vie: JSON.parse(JSON.stringify(data.hygiene_vie)),
        conseils_alimentaires: JSON.parse(JSON.stringify(data.conseils_alimentaires)),
        soutiens_naturels: JSON.parse(JSON.stringify(data.soutiens_naturels)),
        routine_bien_etre: JSON.parse(JSON.stringify(data.routine_bien_etre)),
        is_active: data.is_active,
        is_section_active: checked,
      };

      if (data.id) {
        await supabase
          .from("seasonal_wellness")
          .update({ is_section_active: checked })
          .eq("id", data.id);
      } else {
        // Create the record if it doesn't exist
        const { data: newData } = await supabase
          .from("seasonal_wellness")
          .insert([payload])
          .select()
          .single();
        if (newData) {
          newSeasonalData[season] = { ...newSeasonalData[season], id: newData.id, user_id: newData.user_id };
        }
      }
    }
    
    setSeasonalData(newSeasonalData);
    toast.success(checked ? "Section activée sur le site vitrine" : "Section désactivée");
  };

  const handleSeasonToggle = (season: string, checked: boolean) => {
    setSeasonalData((prev) => ({
      ...prev,
      [season]: { ...prev[season], is_active: checked },
    }));
  };

  const handleFieldChange = (season: string, field: string, value: string) => {
    setSeasonalData((prev) => ({
      ...prev,
      [season]: { ...prev[season], [field]: value },
    }));
  };

  const handleItemChange = (
    season: string,
    category: "hygiene_vie" | "conseils_alimentaires" | "soutiens_naturels" | "routine_bien_etre",
    index: number,
    value: string
  ) => {
    setSeasonalData((prev) => {
      const items = [...prev[season][category]];
      items[index] = { content: value };
      return {
        ...prev,
        [season]: { ...prev[season], [category]: items },
      };
    });
  };

  const addItem = (
    season: string,
    category: "hygiene_vie" | "conseils_alimentaires" | "soutiens_naturels" | "routine_bien_etre"
  ) => {
    setSeasonalData((prev) => ({
      ...prev,
      [season]: {
        ...prev[season],
        [category]: [...prev[season][category], { content: "" }],
      },
    }));
  };

  const removeItem = (
    season: string,
    category: "hygiene_vie" | "conseils_alimentaires" | "soutiens_naturels" | "routine_bien_etre",
    index: number
  ) => {
    setSeasonalData((prev) => {
      const items = prev[season][category].filter((_, i) => i !== index);
      return {
        ...prev,
        [season]: { ...prev[season], [category]: items.length ? items : [{ content: "" }] },
      };
    });
  };

  const saveSeason = async (season: string) => {
    if (!userId) return;
    setSaving(true);

    try {
      const data = seasonalData[season];
      const payload = {
        user_id: userId,
        season,
        title: data.title,
        description: data.description,
        hygiene_vie: JSON.parse(JSON.stringify(data.hygiene_vie)),
        conseils_alimentaires: JSON.parse(JSON.stringify(data.conseils_alimentaires)),
        soutiens_naturels: JSON.parse(JSON.stringify(data.soutiens_naturels)),
        routine_bien_etre: JSON.parse(JSON.stringify(data.routine_bien_etre)),
        is_active: data.is_active,
        is_section_active: isSectionActive,
      };

      if (data.id) {
        const { error } = await supabase
          .from("seasonal_wellness")
          .update(payload)
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const { data: newData, error } = await supabase
          .from("seasonal_wellness")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        setSeasonalData((prev) => ({
          ...prev,
          [season]: { ...prev[season], id: newData.id, user_id: newData.user_id },
        }));
      }

      toast.success(`Protocole ${seasonConfig[season as keyof typeof seasonConfig].name} sauvegardé`);
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Espace bien-être saisonnier"
        subtitle="Gérez vos protocoles bien-être pour chaque saison"
        icon={<Sparkles className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="accent"
        actions={
          <Card className="p-3 sm:p-4 border-0 shadow-card bg-card/80 backdrop-blur-sm">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                {isSectionActive ? (
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                ) : (
                  <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                )}
                <Label htmlFor="section-toggle" className="font-medium text-xs sm:text-sm">
                  Visible sur le site
                </Label>
              </div>
              <Switch
                id="section-toggle"
                checked={isSectionActive}
                onCheckedChange={handleSectionToggle}
              />
            </div>
          </Card>
        }
      />

        {/* Tabs pour chaque saison */}
        <Tabs defaultValue="spring" className="space-y-6">
          {/* Mobile: horizontal scroll tabs */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-4 h-auto p-1 gap-1 min-w-full sm:min-w-0">
              {Object.entries(seasonConfig).map(([season, config]) => {
                const Icon = config.icon;
                const data = seasonalData[season];
                return (
                  <TabsTrigger
                    key={season}
                    value={season}
                    className={`flex items-center gap-1 sm:gap-2 py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap data-[state=active]:${config.bgColor}`}
                  >
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="hidden xs:inline sm:inline">{config.name}</span>
                    {data.is_active && (
                      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {Object.entries(seasonConfig).map(([season, config]) => {
            const Icon = config.icon;
            const data = seasonalData[season];

            return (
              <TabsContent key={season} value={season} className="space-y-6">
                {/* Card principale */}
                <Card className={`border-2 ${data.is_active ? config.borderColor : "border-border"}`}>
                  <CardHeader className="pb-4">
                    {/* Mobile: stack vertically */}
                    <div className="flex flex-col gap-4">
                      {/* Title row */}
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${config.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${config.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg sm:text-xl">{config.name}</CardTitle>
                          <CardDescription className="text-xs sm:text-sm">
                            Protocole bien-être saisonnier
                          </CardDescription>
                        </div>
                      </div>
                      
                      {/* Actions row - responsive */}
                      <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-3 xs:gap-4">
                        <div className="flex items-center justify-between xs:justify-start gap-2">
                          <Label htmlFor={`${season}-toggle`} className="text-xs sm:text-sm whitespace-nowrap">
                            Activer carte
                          </Label>
                          <Switch
                            id={`${season}-toggle`}
                            checked={data.is_active}
                            onCheckedChange={(checked) => handleSeasonToggle(season, checked)}
                          />
                        </div>
                        <Button 
                          onClick={() => saveSeason(season)} 
                          disabled={saving}
                          size="sm"
                          className="w-full xs:w-auto"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Message accrocheur (affiché) */}
                    <div className={`p-4 ${config.bgColor} rounded-lg`}>
                      <p className="text-center italic text-muted-foreground">
                        "Découvrez votre protocole bien-être de {config.name.toLowerCase()}"
                      </p>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label>Description (optionnelle)</Label>
                      <Textarea
                        value={data.description}
                        onChange={(e) => handleFieldChange(season, "description", e.target.value)}
                        placeholder="Description du protocole saisonnier..."
                        rows={3}
                      />
                    </div>

                    {/* Sections du protocole */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {/* Hygiène de vie */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">🌿 Hygiène de Vie</Label>
                        {data.hygiene_vie.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={item.content}
                              onChange={(e) => handleItemChange(season, "hygiene_vie", index, e.target.value)}
                              placeholder="Conseil d'hygiène de vie..."
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(season, "hygiene_vie", index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addItem(season, "hygiene_vie")}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>

                      {/* Conseils alimentaires */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">🍽️ Conseils Alimentaires</Label>
                        {data.conseils_alimentaires.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={item.content}
                              onChange={(e) =>
                                handleItemChange(season, "conseils_alimentaires", index, e.target.value)
                              }
                              placeholder="Conseil alimentaire..."
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(season, "conseils_alimentaires", index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addItem(season, "conseils_alimentaires")}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>

                      {/* Soutiens naturels */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">🌱 Soutiens Naturels</Label>
                        {data.soutiens_naturels.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={item.content}
                              onChange={(e) => handleItemChange(season, "soutiens_naturels", index, e.target.value)}
                              placeholder="Soutien naturel..."
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(season, "soutiens_naturels", index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addItem(season, "soutiens_naturels")}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>

                      {/* Routine bien-être */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">✨ Routine Bien-être</Label>
                        {data.routine_bien_etre.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={item.content}
                              onChange={(e) => handleItemChange(season, "routine_bien_etre", index, e.target.value)}
                              placeholder="Routine bien-être..."
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(season, "routine_bien_etre", index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addItem(season, "routine_bien_etre")}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
    </PageContainer>
  );
};

export default SeasonalWellnessManagement;
